'use client';

import { cloneElement, forwardRef, isValidElement, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDisabledInteractions } from '../../hooks/use-disabled-interactions';
import { VerticalNavContext } from './context/vertical-nav.context';
import { verticalNavItemRecipe } from './styles/vertical-nav-item.styles';
import type { VerticalNavItemPropsInterface } from './types/vertical-nav-item.types';

/**
 * @since 8.14.0
 */
export const VerticalNavItem = forwardRef<HTMLLIElement, VerticalNavItemPropsInterface>(
  (props: VerticalNavItemPropsInterface, forwardedRef) => {
    const {
      children,
      className,
      css: cssProp,
      disabled = false,
      current,
      selected,
      ...rest
    } = props;

    const { tone } = useContext(VerticalNavContext);

    const styles = css(
      verticalNavItemRecipe.raw({
        selected,
        current: current === 'true' || current === 'false' ? !!current : current,
        disabled,
        tone,
      }),
      cssProp,
    );

    // Anchors can't use the native `disabled` attribute, so a disabled nav link
    // is removed from the tab order and has its interactions cancelled in the
    // capture phase.
    const disabledInteractionsProps = useDisabledInteractions({ focusable: false });

    return (
      <li
        ref={forwardedRef}
        data-c11n-component="VerticalNav.Item"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', styles, className)}
        {...(disabled ? { 'aria-disabled': true } : {})}
        {...(selected ? { 'aria-selected': selected } : {})}
        {...(current ? { 'aria-current': current } : {})}
        {...rest}
      >
        {disabled && isValidElement(children) && children.props.href
          ? cloneElement(children, {
              ...disabledInteractionsProps,
            } as any)
          : children}
      </li>
    );
  },
);

VerticalNavItem.displayName = 'VerticalNav.Item';
